import React, { useMemo } from "react";
import type { MatchStatus } from "service";
import MatchTimeline, { MatchTimelineEvent } from "components/matchTimeline";
import { getTeamColors } from "components/colors";
import { makeBasketballTimelineIcons } from "./EventIcons";

const quarterLabelFromTitle = (title: string): string | undefined => {
  const quarterMatch = title.match(
    /\b(1st|2nd|3rd|4th)\s+Quarter\b|\bOT\b|\bOvertime\b/i
  );
  if (!quarterMatch) return undefined;
  const q = quarterMatch[0].toLowerCase();
  if (q.includes("1st")) return "Q1";
  if (q.includes("2nd")) return "Q2";
  if (q.includes("3rd")) return "Q3";
  if (q.includes("4th")) return "Q4";
  if (q.includes("ot") || q.includes("overtime")) return "OT";
  return undefined;
};

const extractScore = (parts: string[]): string | undefined => {
  const scoreboard = parts[parts.length - 1];
  return /\d+\s*-\s*\d+/.test(scoreboard) ? scoreboard : undefined;
};

export const buildBasketballMatchTimelineEvents = (
  status: MatchStatus
): MatchTimelineEvent[] => {
  const raw = (status as any)?.m;
  if (!Array.isArray(raw)) return [];

  const events = raw
    .map((item: any, index: number): MatchTimelineEvent | null => {
      const rawLabel = typeof item?.n === "string" ? item.n.trim() : "";
      if (!rawLabel) return null;

      const parts = rawLabel
        .split(" - ")
        .map((part: string) => part.trim())
        .filter(Boolean);

      if (parts.length === 0) return null;

      const score = extractScore(parts);
      const title = score ? parts.slice(0, -1).join(" - ") : rawLabel;
      const minuteText = quarterLabelFromTitle(title);

      return {
        id: String(item?.id ?? index),
        type: "period_bucket",
        title,
        subtitle: score,
        minuteText,
        raw: rawLabel,
      };
    })
    .filter(Boolean) as MatchTimelineEvent[];

  return events.reverse();
};

type BasketballMatchTimelineProps = {
  events: MatchTimelineEvent[];
  status: MatchStatus;
};

const BasketballMatchTimeline: React.FC<BasketballMatchTimelineProps> = ({
  events,
  status,
}) => {
  const { home, away } = getTeamColors(status as any);
  const icons = useMemo(() => makeBasketballTimelineIcons(), []);

  if (!events.length) return null;

  const marker = { type: "whistle", iconKey: "whistle" } as MatchTimelineEvent;
  const markers = events.length
    ? { top: marker, bottom: marker }
    : { top: undefined, bottom: undefined };

  return (
    <MatchTimeline
      events={events}
      homeColor={home}
      awayColor={away}
      icons={icons}
      title="Match Timeline"
      topMarker={markers.top}
      bottomMarker={markers.bottom}
    />
  );
};

export default BasketballMatchTimeline;
